import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider, Box, Button, IconButton, Tooltip } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

import { Context } from '../../../context/UserContext';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import { useMRTState } from '../../../hooks/useMRTState';

import tableThemeCustom from '../../form/tableThemeCustom';
import CustomDialog from '../../layout/CustomDialog';

import tratar from '../../../utils/retornaTratamentoURL';
import CRUDsAPI from '../../../api/CRUDs';
import { obterPermissoesUsuario } from '../../../utils/funcoes';

function safeBoolLS(key) {
    try {
        return JSON.parse(localStorage.getItem(key));
    } catch {
        return false;
    }
}

const Index = () => {
    const [parametros, setParametros] = useState([]);
    const [token] = useState(localStorage.getItem('token') || '');
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();
    const { parametro } = useContext(Context);

    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const pathname = '/ParametroOperacoes/CreateOrEdit';
    const urlParams = new URLSearchParams(window.location.search);
    const ul = urlParams.get('ul') || 1;
    const li = urlParams.get('li') || 51;
    const tipo = urlParams.get('tipo');
    const operacaoUUID = urlParams.get('operacao');
    const uuid = urlParams.get('uuid');
    const btnText = urlParams.get('btnText') || 'Cadastrar';

    const params = useMemo(
        () =>
            new URLSearchParams({
                ul,
                li,
                tipo: tipo || '',
                operacao: operacaoUUID || '',
                uuid: uuid || '',
                btnText,
            }),
        [ul, li, tipo, operacaoUUID, uuid, btnText]
    );

    const mrt = useMRTState({
        storageKey: 'operacaoParametro.index',
        pageSizeDefault: parametro?.paginacao || 10,
        pageIndexDefault: 0,
    });

    useEffect(() => {
        if (!parametro?.paginacao) return;

        mrt.setPagination((prev) => {
            if (prev?.pageSize && prev.pageSize !== 0) return prev;

            return {
                ...prev,
                pageSize: parametro.paginacao,
            };
        });
    }, [parametro?.paginacao]);

    useEffect(() => {
        const currentPath = window.location.pathname;
        const savedPath = JSON.parse(localStorage.getItem('path') || '""');

        if (savedPath !== currentPath) {
            tratar.LimparFiltros();
            localStorage.setItem('path', JSON.stringify(currentPath));
        }
    }, []);

    useEffect(() => {
        if (!token) {
            navigate('/Auth', { replace: true });
            return;
        }

        obterPermissoesUsuario(localStorage.getItem('pessoaId'), localStorage.getItem('empresaId'), ul, li);
    }, [token, ul, li, navigate]);

    const carregarDados = useCallback(async () => {
        if (mrt.rowCount === 0) {
            mrt.setIsLoading(true);
        } else {
            mrt.setIsRefetching(true);
        }

        try {
            const response = await CRUDsAPI.operacaoParametro.listar(
                mrt.pagination.pageIndex * mrt.pagination.pageSize,
                mrt.pagination.pageSize,
                JSON.stringify(mrt.columnFilters ?? []),
                JSON.stringify(mrt.columnFilterFns ?? {}),
                mrt.globalFilter ?? '',
                JSON.stringify(mrt.sorting ?? [])
            );

            setParametros(response?.data?.data || []);
            mrt.setRowCount(response?.data?.meta?.totalRowCount || 0);
            mrt.setIsError(false);
        } catch (error) {
            console.error(error);
            mrt.setIsError(true);
        } finally {
            mrt.setIsLoading(false);
            mrt.setIsRefetching(false);
        }
    }, [
        mrt.rowCount,
        mrt.pagination.pageIndex,
        mrt.pagination.pageSize,
        mrt.columnFilters,
        mrt.columnFilterFns,
        mrt.globalFilter,
        mrt.sorting,
    ]);

    useEffect(() => {
        if (!token) return;
        carregarDados();
    }, [token, carregarDados]);

    const handleOpenDialog = useCallback((row) => {
        setSelectedRow(row);
        setOpenDialog(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setOpenDialog(false);
        setSelectedRow(null);
    }, []);

    const handleConfirmDialog = useCallback(async () => {
        if (!selectedRow) return;

        try {
            const response = await CRUDsAPI.operacaoParametro.deletar(selectedRow);
            setFlashMessage(response?.data?.message || 'Registro deletado com sucesso.', 'success');
            handleCloseDialog();
            carregarDados();
        } catch (error) {
            console.error(error);
            setFlashMessage(error?.response?.data?.message || 'Erro ao deletar registro.', 'error');
            handleCloseDialog();
        }
    }, [selectedRow, setFlashMessage, handleCloseDialog, carregarDados]);

    const columns = useMemo(
        () => [
            {
                id: 'ParametrosOperacao',
                header: 'Parâmetros das Operações',
                columns: [
                    {
                        accessorKey: 'Operacao.descricao',
                        header: 'Operação',
                    },
                    {
                        accessorKey: 'Operacao.sigla',
                        header: 'Sigla',
                    },
                ],
            },
        ],
        []
    );

    const theme = tableThemeCustom();

    return (
        <div>
            {openDialog && (
                <CustomDialog
                    title="Delete parametro!"
                    content="Você tem certeza?"
                    onClose={handleCloseDialog}
                    onConfirm={handleConfirmDialog}
                />
            )}

            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    columns={columns}
                    data={parametros}
                    localization={MRT_Localization_PT_BR}
                    getRowId={(row) => row.uuid}
                    enableColumnFilterModes
                    enableGlobalFilterModes
                    enableColumnOrdering
                    enableEditing
                    enableGrouping
                    enableRowActions
                    enableRowSelection
                    manualFiltering
                    manualPagination
                    manualSorting
                    rowCount={mrt.rowCount}
                    displayColumnDefOptions={{
                        'mrt-row-actions': {
                            muiTableHeadCellProps: {
                                align: 'center',
                            },
                            size: 50,
                        },
                        'mrt-row-numbers': {
                            enableColumnOrdering: true,
                            enableResizing: true,
                            muiTableHeadCellProps: {
                                sx: {
                                    fontSize: '1.2rem',
                                },
                            },
                        },
                        'mrt-row-select': {
                            enableColumnActions: true,
                            enableHiding: true,
                            size: 10,
                        },
                    }}
                    renderRowActions={({ row }) => (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'nowrap',
                                gap: '1px',
                            }}
                        >
                            <Tooltip arrow placement="top-start" title="Editar">
                                <span>
                                    <IconButton
                                        onClick={() => {
                                            params.set('uuid', row.id);
                                            params.set('btnText', 'Atualizar');
                                            navigate(pathname, params);
                                        }}
                                        disabled={safeBoolLS('editar') === false}
                                    >
                                        <Edit />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip arrow placement="top-start" title="Deletar">
                                <span>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleOpenDialog(row)}
                                        disabled={!(safeBoolLS('isAdmin') === true && safeBoolLS('cancelar') === true)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                    renderTopToolbarCustomActions={() => (
                        <div
                            style={{
                                display: 'flex',
                                gap: '8px',
                            }}
                        >
                            <Tooltip arrow placement="top-end" title="Novo Registro">
                                <span>
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        disabled={!(safeBoolLS('isAdmin') === true && safeBoolLS('incluir') === true)}
                                        onClick={() => navigate(pathname, params)}
                                    >
                                        Novo
                                    </Button>
                                </span>
                            </Tooltip>
                        </div>
                    )}
                    onColumnFiltersChange={mrt.setColumnFilters}
                    onColumnFilterFnsChange={mrt.setColumnFilterFns}
                    onGlobalFilterChange={mrt.setGlobalFilter}
                    onPaginationChange={mrt.setPagination}
                    onSortingChange={mrt.setSorting}
                    initialState={{
                        showColumnFilters: true,
                        density: 'compact',
                    }}
                    state={{
                        columnFilterFns: mrt.columnFilterFns,
                        columnFilters: mrt.columnFilters,
                        globalFilter: mrt.globalFilter,
                        isLoading: mrt.isLoading,
                        pagination: mrt.pagination,
                        showAlertBanner: mrt.isError,
                        showProgressBars: mrt.isRefetching,
                        sorting: mrt.sorting,
                    }}
                    muiToolbarAlertBannerProps={
                        mrt.isError
                            ? {
                                  color: 'error',
                                  children: 'Erro ao carregar os dados',
                              }
                            : undefined
                    }
                    positionToolbarDropZone="bottom"
                    positionExpandColumn="first"
                    positionActionsColumn="first"
                    positionToolbarAlertBanner="bottom"
                />
            </ThemeProvider>
        </div>
    );
};

export default Index;
